import { useCallback, useState } from "react";
import { Loader2, ScanLine, FileUp, ShieldHalf } from "lucide-react";
import { useVault } from "@/context/VaultContext";
import { Switch } from "@/components/ui/switch";
import { QrScanner } from "@/components/aph/QrScanner";

const inputCls = "w-full bg-nebula border border-rim rounded-secure px-3 py-2.5 text-sm text-ash placeholder-ash/30 focus:outline-none focus:border-stellar/50";

function KeyInput({ prefix, value, onChange }) {
  const [scan, setScan] = useState(false);
  const onScan = useCallback((v) => onChange(v.trim()), [onChange]);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onChange((await f.text()).trim());
  };
  return (
    <div className="space-y-2">
      <textarea data-testid={`${prefix}-key-input`} value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder="aphk1.…" spellCheck={false}
        className={`${inputCls} font-mono text-[11px] resize-none break-all`} />
      <div className="flex gap-2">
        <button type="button" onClick={() => setScan(true)} data-testid={`${prefix}-scan-btn`} className="flex-1 flex items-center justify-center gap-1.5 border border-rim rounded-secure py-2 text-xs text-ash/70 hover:text-stellar hover:border-stellar/40"><ScanLine size={14} strokeWidth={1.5} /> Scan QR</button>
        <label className="flex-1 flex items-center justify-center gap-1.5 border border-rim rounded-secure py-2 text-xs text-ash/70 hover:text-stellar hover:border-stellar/40 cursor-pointer" data-testid={`${prefix}-file-btn`}>
          <FileUp size={14} strokeWidth={1.5} /> Load key file
          <input type="file" accept=".txt,.aphkey,text/plain" className="hidden" onChange={onFile} data-testid={`${prefix}-file-input`} />
        </label>
      </div>
      <QrScanner open={scan} onOpenChange={setScan} onResult={onScan} />
    </div>
  );
}

export function LoginForm({ onDone }) {
  const { login } = useVault();
  const [f, setF] = useState({ key: "", pass: "", decoy: false, dKey: "", dPass: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = useCallback((k) => (v) => setF((p) => ({ ...p, [k]: v })), []);
  const setKey = useCallback((v) => setF((p) => ({ ...p, key: v })), []);
  const setDKey = useCallback((v) => setF((p) => ({ ...p, dKey: v })), []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.key.trim() || !f.pass) return setError("Enter your login key and passphrase");
    if (f.decoy && (!f.dKey.trim() || !f.dPass)) return setError("Enter the decoy key and its passphrase");
    setBusy(true);
    try {
      await login({ key: f.key, pass: f.pass, decoy: f.decoy ? { key: f.dKey, pass: f.dPass } : null });
      onDone?.();
    } catch (err) {
      setError(err.message || "Could not log in");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <div>
        <span className="text-[11px] uppercase tracking-[0.16em] text-ash/50 font-branding">Login key</span>
        <div className="mt-1.5"><KeyInput prefix="login" value={f.key} onChange={setKey} /></div>
      </div>
      <label className="block">
        <span className="text-[11px] uppercase tracking-[0.16em] text-ash/50 font-branding">Passphrase or PIN</span>
        <input data-testid="login-pass-input" type="password" value={f.pass} onChange={(e) => set("pass")(e.target.value)} placeholder="The passphrase of that vault" className={`${inputCls} mt-1.5`} />
      </label>
      <div className="bg-nebula border border-rim rounded-secure p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium flex items-center gap-2"><ShieldHalf size={16} strokeWidth={1.5} className="text-stellar" /> Also restore Decoy Vault</p>
          <Switch data-testid="login-decoy-toggle" checked={f.decoy} onCheckedChange={set("decoy")} />
        </div>
        {f.decoy && (
          <div className="space-y-3 rise-in">
            <KeyInput prefix="login-decoy" value={f.dKey} onChange={setDKey} />
            <input data-testid="login-decoy-pass-input" type="password" value={f.dPass} onChange={(e) => set("dPass")(e.target.value)} placeholder="Decoy passphrase" className={inputCls} />
          </div>
        )}
      </div>
      {error && <p className="text-sm text-flare" data-testid="login-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-3 hover:opacity-85 transition-opacity disabled:opacity-60">
        {busy ? <><Loader2 size={16} className="animate-spin" /> Restoring identity…</> : "Log in"}
      </button>
      <p className="text-[11px] text-ash/40 leading-relaxed">Get your login key from Settings → Log in on another device. Contacts and groups come with you; old messages stay on the old device. Use only one device per identity.</p>
    </form>
  );
}
